/** styled-system / RN 样式与业务 props，不可透传到 DOM */
const BORDER_SIDES = ["Top", "Right", "Bottom", "Left"] as const
const BORDER_CORNERS = ["TopLeft", "TopRight", "BottomLeft", "BottomRight"] as const

function buildBorderStyleProps(): string[] {
  const props: string[] = []
  for (const side of BORDER_SIDES) {
    props.push(`border${side}Width`, `border${side}Style`, `border${side}Color`)
  }
  for (const corner of BORDER_CORNERS) {
    props.push(`border${corner}Radius`)
  }
  // RN 逻辑边（RTL）
  props.push(
    "borderStartWidth",
    "borderStartColor",
    "borderEndWidth",
    "borderEndColor",
    "borderStartStartRadius",
    "borderStartEndRadius",
    "borderEndStartRadius",
    "borderEndEndRadius",
  )
  return props
}

export const STYLE_PROPS = new Set([
  // space
  "m",
  "mt",
  "mr",
  "mb",
  "ml",
  "mx",
  "my",
  "margin",
  "marginTop",
  "marginRight",
  "marginBottom",
  "marginLeft",
  "marginX",
  "marginY",
  "p",
  "pt",
  "pr",
  "pb",
  "pl",
  "px",
  "py",
  "padding",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "paddingX",
  "paddingY",
  "gap",
  // layout
  "width",
  "height",
  "minWidth",
  "minHeight",
  "maxWidth",
  "maxHeight",
  "size",
  "display",
  "overflow",
  "overflowX",
  "overflowY",
  "verticalAlign",
  "aspectRatio",
  // flexbox
  "alignItems",
  "alignContent",
  "alignSelf",
  "justifyItems",
  "justifySelf",
  "justifyContent",
  "flex",
  "flexBasis",
  "flexDirection",
  "flexGrow",
  "flexShrink",
  "flexWrap",
  "order",
  // color
  "color",
  "bg",
  "backgroundColor",
  "opacity",
  "tintColor",
  "overlayColor",
  // border（含各边 width/style/color 与圆角）
  "border",
  "borderWidth",
  "borderStyle",
  "borderColor",
  "borderRadius",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderX",
  "borderY",
  ...buildBorderStyleProps(),
  // shadow / elevation
  "shadowColor",
  "shadowOffset",
  "shadowOpacity",
  "shadowRadius",
  "elevation",
  "boxShadow",
  // position
  "position",
  "zIndex",
  "top",
  "right",
  "bottom",
  "left",
  // typography
  "fontSize",
  "fontFamily",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "textAlign",
  "fontStyle",
  "textDecorationLine",
  "textDecorationColor",
  "textDecorationStyle",
  "textTransform",
  "fontVariant",
  // RN Text（numberOfLines 由组件消费，见 NATIVE_ONLY_PROPS）
  "ellipsizeMode",
  "allowFontScaling",
  "selectable",
  "suppressHighlighting",
  "adjustsFontSizeToFit",
  "minimumFontScale",
  // background (styled-system)
  "background",
  "backgroundImage",
  "backgroundSize",
  "backgroundPosition",
  "backgroundRepeat",
  // transform（RN style prop，非 DOM transform 字符串）
  "transform",
])

export const NATIVE_ONLY_PROPS = new Set([
  "onPress",
  "onPressIn",
  "onPressOut",
  "onLongPress",
  "delayPressIn",
  "delayPressOut",
  "delayLongPress",
  "android_ripple",
  "pressRetentionOffset",
  "activeOpacity",
  "hitSlop",
  "keyboardShouldPersistTaps",
  "showsVerticalScrollIndicator",
  "showsHorizontalScrollIndicator",
  "keyboardDismissMode",
  "nestedScrollEnabled",
  "scrollEnabled",
  "bounces",
  "overScrollMode",
  "refreshControl",
  "automaticallyAdjustKeyboardInsets",
  "automaticallyAdjustContentInsets",
  "directionalLockEnabled",
  "pagingEnabled",
  "decelerationRate",
  "scrollEventThrottle",
  "contentInset",
  "contentOffset",
  "contentInsetAdjustmentBehavior",
  "maintainVisibleContentPosition",
  "indicatorStyle",
  "persistentScrollbar",
  "snapToInterval",
  "snapToAlignment",
  "centerContent",
  "onScroll",
  "onMomentumScrollBegin",
  "onMomentumScrollEnd",
  "onScrollBeginDrag",
  "onScrollEndDrag",
  "onContentSizeChange",
  "endFillColor",
  "alwaysBounceVertical",
  "alwaysBounceHorizontal",
  "ellipsizeMode",
  "numberOfLines",
  "allowFontScaling",
  "selectable",
  "suppressHighlighting",
  "adjustsFontSizeToFit",
  "minimumFontScale",
  "secureTextEntry",
  "multiline",
  "editable",
  "onChangeText",
  "onSubmitEditing",
  "keyboardType",
  "autoCapitalize",
  "autoCorrect",
  "placeholderTextColor",
  "returnKeyType",
  "blurOnSubmit",
  "clearButtonMode",
  "textContentType",
  "autoComplete",
  "importantForAutofill",
  "enablesReturnKeyAutomatically",
  "underlineColorAndroid",
  "includeFontPadding",
  "textAlignVertical",
  "clearTextOnFocus",
  "submitBehavior",
  "returnKeyLabel",
  "onSelectionChange",
  "onKeyPress",
  "onStartShouldSetResponder",
  "onStartShouldSetResponderCapture",
  "onMoveShouldSetResponder",
  "onMoveShouldSetResponderCapture",
  "onResponderGrant",
  "onResponderMove",
  "onResponderRelease",
  "onResponderTerminate",
  "onResponderTerminationRequest",
  "onEndReached",
  "onEndReachedThreshold",
  "keyExtractor",
  "ListHeaderComponent",
  "ListFooterComponent",
  "ListEmptyComponent",
  "ItemSeparatorComponent",
  "numColumns",
  "columnWrapperStyle",
  "extraData",
  "initialNumToRender",
  "maxToRenderPerBatch",
  "windowSize",
  "removeClippedSubviews",
  "getItemLayout",
  "inverted",
  "data",
  "renderItem",
  "source",
  "resizeMode",
  "onLoadEnd",
  "onError",
  "imageStyle",
  "horizontal",
  "contentContainerStyle",
  "keyboardVerticalOffset",
  "behavior",
  "accessibilityRole",
  "accessibilityState",
  "accessibilityHint",
  "accessibilityValue",
  "accessibilityActions",
  "accessibilityLiveRegion",
  "accessibilityViewIsModal",
  "importantForAccessibility",
  "accessibilityElementsHidden",
  "accessibilityLabelledBy",
])

/** 组件自定义 props（用于 styled 模板，不进 DOM） */
export const CUSTOM_PROPS = new Set([
  "isFocus",
  "error",
  "label",
  "validRule",
  "rightAccessory",
  "required",
  "loading",
  "loadingText",
  "tx",
  "text",
  "textStyle",
  "preset",
  "type",
  "Loading",
  "src",
  "justify",
  "disableBackdropClose",
  "active",
  "item",
  "canSubmit",
  "isFocused",
  "isInvalid",
  "caretHidden",
  "selectionColor",
  "selectTextOnFocus",
  "contextMenuHidden",
  "showSoftInputOnFocus",
  "listOpen",
  "byMe",
  "reply",
  "isNoBg",
  "isPlaying",
])

const DOM_BLOCK = new Set([...STYLE_PROPS, ...NATIVE_ONLY_PROPS, ...CUSTOM_PROPS])

/** styled-components 自行处理，禁止透传到 DOM（数组 style 会触发 CSSStyleDeclaration 报错） */
const STYLED_INTERNAL_PROPS = new Set(["style", "className"])

/** 仅过滤 styled-system / 业务 props，不拦截 onPress / onClick 等事件 */
export function shouldForwardProp(prop: string) {
  return (
    !STYLE_PROPS.has(prop) &&
    !CUSTOM_PROPS.has(prop) &&
    !STYLED_INTERNAL_PROPS.has(prop)
  )
}

export function filterDomProps(props: Record<string, unknown>) {
  const {
    style,
    pointerEvents,
    testID,
    accessible,
    accessibilityLabel,
    onLayout,
    collapsable,
    ...rest
  } = props
  void style
  void pointerEvents
  void testID
  void accessible
  void accessibilityLabel
  void onLayout
  void collapsable

  const domProps: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(rest)) {
    if (!DOM_BLOCK.has(key)) {
      domProps[key] = value
    }
  }
  return domProps
}
